import {
  Card,
  Divider,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  useTheme,
} from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { BoxScroller } from 'styles/elements/styleElements'
import { StyledCardContent } from './ReusableUI'

const determineRoute = item => {
  if (item?.SK?.startsWith('PRODUCT_')) return
  if (item?.SK?.startsWith('NOTIFY_')) return
  if (item?.SK?.startsWith?.('CREATION_TIMESTAMP_')) return `/organizations/${item?.PK}/details`
  if (item?.SK?.startsWith?.('DEAL_')) return `/deals/${item?.PK}/${item?.SK}/details`
  if (item?.SK?.startsWith?.('TASK_') && (!item?.isMarketing || item?.isCreative || item?.type === 'website'))
    return `/projects/${item?.PK}/${item?.SK}/main`
  if (item?.SK?.startsWith?.('SUBTASK_') && (!item?.isMarketing || item?.isCreative || item?.type === 'website'))
    return `/projects/${item?.PK}/${item?.parentTaskSK}/main`
  if (item?.SK?.startsWith?.('TASK_') && item?.isMarketing) return `/teamtasks/${item?.PK}/${item?.SK}/main`
  if (item?.SK?.startsWith?.('SUBTASK_') && item?.isMarketing)
    return `/teamtasks/${item?.PK}/${item?.parentTaskSK}/main`
  if (item?.SK?.startsWith?.('MONTHLY_TASK_') && item?.isDmsMonthlyTask) return `/mtb/${item?.PK}/DMS`
  if (item?.SK?.startsWith?.('MONTHLY_TASK_') && item?.isAmMonthlyTask) return `/mtb/${item?.PK}/AM`
  if (item?.SK?.startsWith?.('MONTHLY_TASK_') && item?.isSeoMonthlyTask) return `/mtb/${item?.PK}/SEO`
  if (item?.contacts_uuid) return `/contacts/${item?.contacts_uuid}/details`
}

/**
 * Skeleton row shown when the list is loading and has no items to display yet.
 * Matches the density of a real ListItem so the card doesn't jank when data
 * arrives — the skeleton rows swap for real rows with no layout shift.
 */
const SkeletonRow = () => (
  <ListItem sx={{ px: 1, py: 0.5 }}>
    <ListItemText
      primary={<Skeleton variant="text" width="70%" animation="wave" />}
      secondary={<Skeleton variant="text" width="45%" animation="wave" />}
      secondaryTypographyProps={{ component: 'div' }}
    />
  </ListItem>
)

/**
 * Dashboard list tile.
 *
 * `isLoading` shows a thin LinearProgress at the top of the card (whether
 * or not there are items) plus 4 skeleton rows in place of "No items"
 * when the list is empty. Both signals fire while the background
 * /search/all fetch is still filling in the products / parentClients
 * data that most of these lists depend on.
 */
const CompactList = ({
  title,
  filter,
  items,
  primary,
  secondary,
  maxHeight = 320,
  isLoading = false,
}: {
  title: string | React.ReactNode
  filter?: React.ReactNode
  items: any[]
  primary: (item: any) => React.ReactNode
  secondary?: (item: any) => React.ReactNode
  maxHeight?: number
  isLoading?: boolean
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const handleNavigate = (route: string) => {
    navigate(route)
  }
  const showSkeletons = isLoading && (!items || items.length === 0)
  return (
    <Card
      sx={{
        borderRadius: 3,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {isLoading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            opacity: 0.6,
            zIndex: 1,
          }}
        />
      )}
      <StyledCardContent sx={{ p: 1.5 }}>
        <Box
          sx={{
            px: 0.5,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
            }}
          >
            {filter}
          </Box>
          {title}
        </Box>
        <Divider />
        <BoxScroller sx={{ maxHeight, overflowY: 'auto', pt: 0.5 }}>
          <List dense disablePadding sx={{ overflowX: 'hidden' }}>
            {showSkeletons ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : items.length ? (
              items.map((it, idx) => {
                const route = determineRoute(it)
                return (
                  <React.Fragment
                    key={it.SK && it.PK ? `${it.PK}-${it.SK}` : it.deal_uuid || it.product_uuid || it.org_uuid || idx}
                  >
                    <ListItem
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 2,
                        mx: 0.5,
                        '&:hover': { background: theme.palette.action.hover },
                        cursor: 'pointer',
                      }}
                      onClick={() => (route ? handleNavigate(route) : null)}
                    >
                      <ListItemText
                        primaryTypographyProps={{
                          noWrap: true,
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                        secondaryTypographyProps={{
                          component: 'div',
                          sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            opacity: 0.8,
                          },
                        }}
                        primary={primary(it)}
                        secondary={secondary ? secondary(it) : undefined}
                      />
                    </ListItem>
                    {idx < items.length - 1 && <Divider component="li" sx={{ my: 0.25 }} />}
                  </React.Fragment>
                )
              })
            ) : (
              <ListItem sx={{ px: 1 }}>
                <ListItemText primary="No items" primaryTypographyProps={{ sx: { opacity: 0.7 } }} />
              </ListItem>
            )}
          </List>
        </BoxScroller>
      </StyledCardContent>
    </Card>
  )
}
export default CompactList
